import { Asignatura, Evaluacion, EscalaCalificacion, NivelCalificacion, PlanEstudio, AcreditacionLibre } from './types';

export const DEFAULT_ESCALA_FCEA: EscalaCalificacion = {
  id: 'escala_fcea_2025',
  facultad: 'FCEA',
  nombre: 'Escala Conceptual Udelar (FCEA)',
  niveles: [
    { concepto: 'Excelente',        porcentaje_min: 87.5, valor_numerico: 10,   tipo: 'aprobacion' },
    { concepto: 'Muy Bueno',        porcentaje_min: 75,   valor_numerico: 8.67, tipo: 'aprobacion' },
    { concepto: 'Bueno',            porcentaje_min: 62.5, valor_numerico: 7.33, tipo: 'aprobacion' },
    { concepto: 'Aceptable',        porcentaje_min: 50,   valor_numerico: 6,    tipo: 'aprobacion' },
    { concepto: 'Insuficiente',     porcentaje_min: 25,   valor_numerico: 3,    tipo: 'reprobacion' },
    { concepto: 'Muy Insuficiente', porcentaje_min: 0,    valor_numerico: 0,    tipo: 'reprobacion' }
  ]
};

export const DEFAULT_ESCALA_PSICO: EscalaCalificacion = {
  id: 'escala_psico_default',
  facultad: 'Psicología',
  nombre: 'Escala Editable (Facultad de Psicología)',
  niveles: [
    { concepto: 'Excelente',        porcentaje_min: 87.5, valor_numerico: 10,   tipo: 'aprobacion' },
    { concepto: 'Muy Bueno',        porcentaje_min: 75,   valor_numerico: 8.67, tipo: 'aprobacion' },
    { concepto: 'Bueno',            porcentaje_min: 62.5, valor_numerico: 7.33, tipo: 'aprobacion' },
    { concepto: 'Aceptable',        porcentaje_min: 50,   valor_numerico: 6,    tipo: 'aprobacion' },
    { concepto: 'Insuficiente',     porcentaje_min: 25,   valor_numerico: 3,    tipo: 'reprobacion' },
    { concepto: 'Muy Insuficiente', porcentaje_min: 0,    valor_numerico: 0,    tipo: 'reprobacion' }
  ]
};

/**
 * Resuelve el nivel conceptual y su valor numérico a partir del porcentaje de logro
 * usando la escala vigente para la facultad.
 */
export function resolverConcepto(
  porcentaje: number, 
  escala: EscalaCalificacion = DEFAULT_ESCALA_FCEA
): NivelCalificacion {
  const nivelesOrdenados = [...escala.niveles].sort((a, b) => b.porcentaje_min - a.porcentaje_min);
  for (const nivel of nivelesOrdenados) {
    if (porcentaje >= nivel.porcentaje_min) {
      return nivel;
    }
  }
  return nivelesOrdenados[nivelesOrdenados.length - 1];
}

/**
 * Resuelve el NivelCalificacion correspondiente a un ítem (evaluación o asignatura)
 * soportando porcentaje_logro, concepto directo o nota numérica legacy (0-12 / 0-100).
 */
export function resolverCalificacionItem(
  item: { porcentaje_logro?: number; concepto?: string; concepto_final?: string; nota?: number; nota_final?: number; escala?: string },
  escala: EscalaCalificacion = DEFAULT_ESCALA_FCEA
): NivelCalificacion | null {
  const pct = item.porcentaje_logro;
  if (pct !== undefined && pct !== null) {
    return resolverConcepto(pct, escala);
  }

  const concepto = item.concepto || item.concepto_final;
  if (concepto) {
    const buscado = escala.niveles.find(n => n.concepto.toLowerCase() === concepto.trim().toLowerCase());
    if (buscado) return buscado;
  }

  const nota = item.nota !== undefined && item.nota !== null ? item.nota : item.nota_final;
  if (nota !== undefined && nota !== null) {
    let porcentajeEquiv = nota;
    if (item.escala === '0-100') {
      porcentajeEquiv = nota;
    } else if (nota <= 12) {
      porcentajeEquiv = (nota / 12) * 100;
    }
    return resolverConcepto(porcentajeEquiv, escala);
  }

  return null;
}

export function calcularPromedioPonderado(evaluaciones: Evaluacion[], escala: EscalaCalificacion = DEFAULT_ESCALA_FCEA): number {
  const califs = evaluaciones.map(e => resolverCalificacionItem(e, escala)).filter((c): c is NivelCalificacion => c !== null);
  if (califs.length === 0) return 0;
  const suma = califs.reduce((acc, c) => acc + c.valor_numerico, 0);
  return suma / califs.length;
}

interface ActividadCalculada {
  id: string;
  nombre: string;
  creditos: number;
  nivel: NivelCalificacion;
  esAprobada: boolean;
  esAsignatura: boolean;
}

function obtenerTodasLasActividades(
  asignaturas: Asignatura[], 
  evaluaciones: Evaluacion[], 
  escala: EscalaCalificacion = DEFAULT_ESCALA_FCEA
): ActividadCalculada[] {
  const actividades: ActividadCalculada[] = [];
  const asignaturasActivas = asignaturas.filter(a => !a.en_papelera);
  const activeAsigIds = new Set(asignaturasActivas.map(a => a.id));
  const evaluacionesActivas = evaluaciones.filter(e => activeAsigIds.has(e.asignatura_id));

  for (const asig of asignaturasActivas) {
    const evalsAsig = evaluacionesActivas.filter(e => e.asignatura_id === asig.id);
    const califAsig = resolverCalificacionItem(asig, escala);

    if (califAsig) {
      actividades.push({
        id: asig.id,
        nombre: asig.nombre,
        creditos: asig.creditos,
        nivel: califAsig,
        esAprobada: asig.estado === 'aprobada' || califAsig.tipo === 'aprobacion',
        esAsignatura: true
      });
    } else if (evalsAsig.length > 0) {
      const califsEvals = evalsAsig.map(e => resolverCalificacionItem(e, escala)).filter((c): c is NivelCalificacion => c !== null);
      if (califsEvals.length > 0) {
        const sumaValores = califsEvals.reduce((s, c) => s + c.valor_numerico, 0);
        const valorPromedio = sumaValores / califsEvals.length;
        const pctEquiv = (valorPromedio / 10) * 100;
        const nivelPromedio = resolverConcepto(pctEquiv, escala);

        actividades.push({
          id: asig.id,
          nombre: asig.nombre,
          creditos: asig.creditos,
          nivel: nivelPromedio,
          esAprobada: asig.estado === 'aprobada' || nivelPromedio.tipo === 'aprobacion',
          esAsignatura: true
        });
      }
    } else if (asig.estado === 'aprobada') {
      const nivelDefault = escala.niveles.find(n => n.concepto === 'Aceptable') || escala.niveles[3];
      actividades.push({
        id: asig.id,
        nombre: asig.nombre,
        creditos: asig.creditos,
        nivel: nivelDefault,
        esAprobada: true,
        esAsignatura: true
      });
    }
  }

  // Optimización js-set-map-lookups: Set de IDs de asignaturas procesadas para búsqueda O(1)
  const procesadasAsigIds = new Set(actividades.map(a => a.id));

  for (const ev of evaluacionesActivas) {
    if (!procesadasAsigIds.has(ev.asignatura_id)) {
      const calif = resolverCalificacionItem(ev, escala);
      if (calif) {
        actividades.push({
          id: ev.id,
          nombre: ev.nombre,
          creditos: 0,
          nivel: calif,
          esAprobada: calif.tipo === 'aprobacion',
          esAsignatura: false
        });
      }
    }
  }

  return actividades;
}

/**
 * 1. Promedio general: todas las actividades (aprobadas y reprobadas) por igual.
 */
export function calcularPromedioGeneral(
  asignaturas: Asignatura[],
  evaluaciones: Evaluacion[],
  escala: EscalaCalificacion = DEFAULT_ESCALA_FCEA
): number {
  const actividades = obtenerTodasLasActividades(asignaturas, evaluaciones, escala);
  if (actividades.length === 0) return 0;

  const suma = actividades.reduce((acc, act) => acc + act.nivel.valor_numerico, 0);
  return suma / actividades.length;
}

/**
 * 2. Promedio de aprobaciones: solo las actividades aprobadas.
 */
export function calcularPromedioAprobaciones(
  asignaturas: Asignatura[],
  evaluaciones: Evaluacion[],
  escala: EscalaCalificacion = DEFAULT_ESCALA_FCEA
): number {
  const actividades = obtenerTodasLasActividades(asignaturas, evaluaciones, escala)
    .filter(act => act.esAprobada);

  if (actividades.length === 0) return 0;

  const suma = actividades.reduce((acc, act) => acc + act.nivel.valor_numerico, 0);
  return suma / actividades.length;
}

/**
 * 3. Promedio ponderado por créditos: solo aprobadas, pesando más las de más créditos.
 */
export function calcularPromedioPonderadoCreditos(
  asignaturas: Asignatura[],
  evaluaciones: Evaluacion[],
  escala: EscalaCalificacion = DEFAULT_ESCALA_FCEA
): number {
  const aprobadasConCreditos = obtenerTodasLasActividades(asignaturas, evaluaciones, escala)
    .filter(act => act.esAprobada && act.creditos > 0);

  if (aprobadasConCreditos.length === 0) return 0;

  const sumaPonderada = aprobadasConCreditos.reduce((acc, act) => acc + (act.nivel.valor_numerico * act.creditos), 0);
  const totalCreditos = aprobadasConCreditos.reduce((acc, act) => acc + act.creditos, 0);

  return totalCreditos > 0 ? sumaPonderada / totalCreditos : 0;
}

/**
 * Gráfico de barras de frecuencia por concepto (conteo en cada uno de los 6 niveles).
 */
export function calcularFrecuenciaConceptos(
  asignaturas: Asignatura[],
  evaluaciones: Evaluacion[],
  escala: EscalaCalificacion = DEFAULT_ESCALA_FCEA
): Record<string, number> {
  const frecuencias: Record<string, number> = {};
  for (const nivel of escala.niveles) {
    frecuencias[nivel.concepto] = 0;
  }

  const actividades = obtenerTodasLasActividades(asignaturas, evaluaciones, escala);
  for (const act of actividades) {
    if (frecuencias[act.nivel.concepto] !== undefined) {
      frecuencias[act.nivel.concepto] += 1;
    } else {
      frecuencias[act.nivel.concepto] = 1;
    }
  }

  return frecuencias;
}

export function calcularCreditosAcumulados(asignaturas: Asignatura[], departamento?: string): number {
  return asignaturas.filter(a => !a.en_papelera).reduce((total, a) => {
    if (a.estado === 'aprobada') {
      if (!departamento || a.departamento === departamento) {
        return total + a.creditos;
      }
    }
    return total;
  }, 0);
}

export function calcularPorcentajeAvance(plan: PlanEstudio, asignaturas: Asignatura[]): number {
  const asignaturasDelPlan = asignaturas.filter(a => a.plan_id === plan.id && !a.en_papelera);
  const creditosTotales = asignaturasDelPlan.reduce((acc, a) => acc + a.creditos, 0);
  if (creditosTotales === 0) return 0;

  const creditosAprobados = asignaturasDelPlan.reduce((acc, a) => {
    if (a.estado === 'aprobada') {
      return acc + a.creditos;
    }
    return acc;
  }, 0);

  return (creditosAprobados / creditosTotales) * 100;
}

/**
 * Calcula la suma total de créditos de asignaturas aprobadas vinculadas a un modulo_id
 * sumando además las acreditaciones libres asignadas a dicho módulo.
 */
export function calcularCreditosPorModulo(
  asignaturas: Asignatura[],
  acreditacionesLibres: AcreditacionLibre[] = [],
  moduloId: string
): number {
  const deptMatch = (a: Asignatura): boolean => {
    if (a.modulo_id === moduloId) return true;
    if (a.modulo_id) return false; // Ya tiene otro módulo explícito
    const d = a.departamento || '';
    if (moduloId === 'mod_fcea_admin') return d === 'Administración';
    if (moduloId === 'mod_fcea_conta') return d === 'Contabilidad';
    if (moduloId === 'mod_fcea_metcant') return d === 'Métodos Cuantitativos';
    if (moduloId === 'mod_fcea_juri') return d.startsWith('Jurídic');
    if (moduloId === 'mod_fcea_integradoras') return d === 'Interdisciplinario';
    if (moduloId === 'mod_fcea_econ') return d === 'Economía';
    if (moduloId === 'mod_fcea_cdf') return Boolean(a.es_opcional);
    return false;
  };

  const creditosAsignaturas = asignaturas
    .filter(a => !a.en_papelera && a.estado === 'aprobada' && deptMatch(a))
    .reduce((acc, a) => acc + a.creditos, 0);

  const creditosLibres = acreditacionesLibres
    .filter(al => al.modulo_id === moduloId)
    .reduce((acc, al) => acc + al.creditos, 0);

  return creditosAsignaturas + creditosLibres;
}

/**
 * Calcula la suma total de créditos de asignaturas aprobadas vinculadas a un ciclo_id.
 */
export function calcularCreditosPorCiclo(
  asignaturas: Asignatura[],
  _acreditacionesLibres: AcreditacionLibre[] = [],
  cicloId: string
): number {
  return asignaturas
    .filter(a => !a.en_papelera && a.ciclo_id === cicloId && a.estado === 'aprobada')
    .reduce((acc, a) => acc + a.creditos, 0);
}


